import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { unsubscribeAll } from 'src/app/common/app.helpers';
import { IDeactivate } from 'src/app/common/guards/save-data-guard/save-data.interface';
import { getLocation } from 'src/app/components/map/shared/location.service';
import { SendMessageFormService } from 'src/app/forms/conversation-forms/send-message-form.service';
import { ViewProfileContent, ViewProfileType } from 'src/app/interfaces/profile.interface';
import { FullUser } from 'src/app/swagger-api';
import { IndividualRoles, ViewProfileTypes } from '../../profile.enum';
import { ProfileService } from '../../profile.service';
import { ViewProfileService } from './view-profile.service';

@Component({
  selector: 'app-view-profile',
  templateUrl: './view-profile.component.html',
  styleUrls: ['./view-profile.component.scss'],
})
export class ViewProfileComponent implements OnInit, OnDestroy, IDeactivate {
  public user: FullUser | null = null;
  public isFormChanged = false;
  public locationUser = '';
  public currentType: ViewProfileTypes | null = null;
  public activeUserRoleTab: IndividualRoles | null = null;

  public readonly ViewProfileTypes: typeof ViewProfileTypes = ViewProfileTypes;
  public readonly content$: Observable<ViewProfileContent> = inject(ViewProfileService).fieldsChanges;
  public readonly isLoading$: Observable<boolean> = inject(ProfileService).isLoading$;

  private subscriptions$: Subscription[] = [];

  private route = inject(ActivatedRoute);
  private viewProfileService = inject(ViewProfileService);
  private profileService = inject(ProfileService);
  private sessionService = inject(SessionService);
  private sendMessageFormService = inject(SendMessageFormService);

  public ngOnInit(): void {
    this.subscribeToRouteChanges();
    this.subscribeToModalWindowChanges();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public setDefaultValueForModal(): void {
    this.sendMessageFormService.resetFormStatus();
  }

  public onActiveTabChanged(activeRoleTabId: IndividualRoles): void {
    this.activeUserRoleTab = activeRoleTabId;
  }

  public onSendMessage(): void {
    this.subscriptions$.push(
      this.viewProfileService.sendMessage(this.user).subscribe((channelUniqueName: string) => {
        this.profileService.userInfo.chat = channelUniqueName;
      }),
    );
  }

  public onMoreInfoOpen({ fields, displayedName }: ViewProfileType): void {
    this.viewProfileService.seeMoreProfileInfo(fields, displayedName);
  }

  public onRelationStatusChanged(): void {
    this.profileService.fetchUserInfo(this.user.user_id);
  }

  private isShowUserCountry(): boolean {
    if (this.profileService.userInfo) {
      return (
        this.profileService.userInfo.profile.location &&
        this.profileService.userInfo.profile.location.country &&
        this.profileService.userInfo.user_roles.length > 0
      );
    }

    return false;
  }

  private subscribeToProfileServiceChanges(): void {
    this.subscriptions$.push(
      this.profileService.profileUserChanges$.subscribe((type: ViewProfileTypes) => {
        this.currentType = type;
        this.setupProfileInfo();
        if (this.isShowUserCountry()) {
          this.locationUser = getLocation(this.profileService.userInfo.profile.location, { includeStreet: true });
        }
      }),
    );
  }

  private setupProfileInfo(): void {
    this.user = this.profileService.userInfo;
    const isOwner: boolean = !this.user.user_id || this.user.user_id === this.sessionService.userId;
    this.viewProfileService.setupProfileInfo(isOwner, this.profileService.userInfo, this.profileService.userRolesInfo);
  }

  private subscribeToRouteChanges(): void {
    this.subscriptions$.push(
      this.route.params.subscribe((params: Params) => {
        this.subscribeToProfileServiceChanges();
        //  if route params without id - it means that visitor is profile owner and we use session user id
        const id: string = (params['id'] as string) || this.sessionService.userId;
        this.profileService.setupUserInfo(id);
      }),
    );
  }

  private subscribeToModalWindowChanges(): void {
    this.subscriptions$.push(
      this.sendMessageFormService.valueChanged.subscribe((value: boolean) => {
        this.isFormChanged = value;
      }),
    );
  }
}
